import React, { useState, useEffect } from 'react';
import { DocPage, PageHeader, Section, Para, Prerequisites } from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './RateCard.css';

const CALC_TYPES = [
  { id: 'fixed',       label: 'Fixed' },
  { id: 'ratePerUnit', label: 'Per Unit' },
  { id: 'graduated',   label: 'Graduated' },
  { id: 'volume',      label: 'Volume' },
  { id: 'minMax',      label: 'Min / Max' },
];

// ── Calculation logic ─────────────────────────────────────────────────────────

function runFixed(price) {
  return { total: price, steps: [{ label: 'Fixed fee', amount: price }] };
}

function runRatePerUnit(rate, usage) {
  const total = rate * usage;
  return { total, steps: [{ label: `${usage} × ${rate}`, amount: total }] };
}

function runGraduated(tiers, usage) {
  let remaining = usage, total = 0;
  const steps = [];
  for (let i = 0; i < tiers.length; i++) {
    if (remaining <= 0) break;
    const t    = tiers[i];
    const r    = parseFloat(t.rate || 0);
    const flat = parseFloat(t.flat || 0);
    const isRepeat = t.repeat && i === tiers.length - 1;

    if (isRepeat) {
      const bracketSize = Number(t.limit) - t.start;
      if (bracketSize <= 0) break;
      let bracketStart = t.start;
      while (remaining > 0) {
        const consumed = Math.min(remaining, bracketSize);
        const tierTotal = consumed * r + flat;
        total += tierTotal;
        const detail = flat > 0 ? `${consumed} × ${r} + ${flat} flat` : `${consumed} × ${r}`;
        steps.push({ label: `Units ${bracketStart + 1}–${bracketStart + consumed}`, detail, amount: tierTotal });
        remaining    -= consumed;
        bracketStart += bracketSize;
      }
    } else {
      const limit    = t.limit === null ? Infinity : Number(t.limit);
      const consumed = Math.min(remaining, limit - t.start);
      if (consumed <= 0) break;
      const tierTotal = consumed * r + flat;
      total += tierTotal;
      const detail = flat > 0 ? `${consumed} × ${r} + ${flat} flat` : `${consumed} × ${r}`;
      steps.push({ label: `Units ${t.start + 1}–${t.start + consumed}`, detail, amount: tierTotal });
      remaining -= consumed;
    }
  }
  return { total, steps };
}

function runVolume(tiers, usage) {
  let matched = tiers[tiers.length - 1];
  for (const t of tiers) {
    if (usage <= (t.limit === null ? Infinity : Number(t.limit))) { matched = t; break; }
  }
  const r    = parseFloat(matched.rate || 0);
  const flat = parseFloat(matched.flat || 0);
  const charge = usage * r;
  const label  = matched.limit === null ? `${matched.start}+` : `${matched.start}–${matched.limit}`;
  const steps  = [{ label: `All ${usage} units (tier ${label})`, detail: `${usage} × ${r}`, amount: charge }];
  if (flat > 0) steps.push({ label: 'Tier flat fee', amount: flat });
  return { total: charge + flat, steps };
}

function runMinMax(flatFee, rate, variant, usage) {
  const r  = typeof rate === 'string' ? parseFloat(rate) : rate;
  const ff = typeof flatFee === 'string' ? parseFloat(flatFee) : flatFee;
  const usageBased = usage * r;
  const total = (variant === 'max' ? Math.max : Math.min)(ff, usageBased);
  const fn = variant === 'max' ? 'Max' : 'Min';
  return {
    total,
    steps: [
      { label: 'Flat fee',                              amount: ff },
      { label: `${usage} × ${r}`,                       amount: usageBased },
      { label: `${fn}(${ff}, ${usageBased})`,           amount: total, isFinal: true },
    ],
  };
}

// ── Tier editor ───────────────────────────────────────────────────────────────

function TierEditor({ tiers, onChange, allowRepeat = false }) {
  const recomputeStarts = (ts) =>
    ts.map((t, i) => ({ ...t, start: i === 0 ? 0 : Number(ts[i - 1].limit ?? ts[i - 1].start) }));

  const setLimit = (i, val) => {
    const next = tiers.map((t, idx) => idx === i ? { ...t, limit: val === '' ? null : val } : t);
    onChange(recomputeStarts(next));
  };
  const setRate = (i, val) =>
    onChange(tiers.map((t, idx) => idx === i ? { ...t, rate: val } : t));
  const setFlat = (i, val) =>
    onChange(tiers.map((t, idx) => idx === i ? { ...t, flat: val } : t));

  const toggleRepeat = () => {
    const last = tiers.length - 1;
    const t = tiers[last];
    if (t.repeat) {
      onChange(tiers.map((tier, idx) => idx === last ? { ...tier, repeat: false, limit: null } : tier));
    } else {
      const prevSize = last > 0 ? (Number(tiers[last - 1].limit) - tiers[last - 1].start) : 50;
      const bracketSize = prevSize > 0 ? prevSize : 50;
      onChange(tiers.map((tier, idx) =>
        idx === last ? { ...tier, repeat: true, limit: tier.start + bracketSize } : tier
      ));
    }
  };

  const addTier = () => {
    const last    = tiers.length - 1;
    const lastTier = tiers[last];
    if (lastTier.repeat) {
      // Convert repeat tier to normal, add new ∞ tier
      onChange([
        ...tiers.slice(0, -1),
        { ...lastTier, repeat: false },
        { start: Number(lastTier.limit), limit: null, rate: lastTier.rate, flat: 0 },
      ]);
    } else {
      const lastFinite = tiers[last - 1];
      const newLimit   = Number(lastFinite?.limit ?? 0) + 50;
      onChange([...tiers.slice(0, -1),
        { start: Number(lastFinite?.limit ?? 0), limit: newLimit, rate: lastTier.rate, flat: 0 },
        { ...lastTier, start: newLimit },
      ]);
    }
  };

  const removeTier = (i) => {
    if (tiers.length <= 2) return;
    const filtered   = tiers.filter((_, idx) => idx !== i);
    const recomputed = recomputeStarts(filtered);
    if (i === tiers.length - 1) {
      const last = recomputed.length - 1;
      recomputed[last] = { ...recomputed[last], limit: null, repeat: false };
    }
    onChange(recomputed);
  };

  const lastIdx = tiers.length - 1;

  return (
    <div className="rc-tier-editor">
      <div className={`rc-tier-head${allowRepeat ? '' : ' rc-tier-row--no-rep'}`}>
        <span>From</span><span>To</span><span>Rate</span><span>Flat</span>
        {allowRepeat && <span>Rep</span>}
        <span />
      </div>
      {tiers.map((t, i) => (
        <div key={i} className={`rc-tier-row${allowRepeat ? '' : ' rc-tier-row--no-rep'}`}>
          <span className="rc-tier-from">{t.start}</span>
          {t.limit === null
            ? <span className="rc-tier-inf">∞</span>
            : <input className="rc-tier-input" type="number" min={t.start + 1} value={t.limit}
                onChange={(e) => setLimit(i, e.target.value)} />
          }
          <input className="rc-tier-input" type="number" min="0" step="0.01" value={t.rate}
            onChange={(e) => setRate(i, e.target.value)} />
          <input className="rc-tier-input" type="number" min="0" step="0.01" value={t.flat ?? 0}
            onChange={(e) => setFlat(i, e.target.value)} />
          {allowRepeat && (i === lastIdx
            ? <button
                className={`rc-tier-repeat${t.repeat ? ' rc-tier-repeat--on' : ''}`}
                onClick={toggleRepeat}
                title={t.repeat ? 'Repeating bracket (click to disable)' : 'Make repeating bracket'}
              >↺</button>
            : <span />
          )}
          <button className="rc-tier-remove" onClick={() => removeTier(i)}
            disabled={tiers.length <= 2} title="Remove tier">×</button>
        </div>
      ))}
      <button className="rc-tier-add" onClick={addTier}>+ Add tier</button>
    </div>
  );
}

// ── Result panel ──────────────────────────────────────────────────────────────

function ResultPanel({ result, currency, empty }) {
  const fmt = (n) => {
    const v = Number(n);
    if (isNaN(v)) return `${currency}—`;
    return `${currency}${v % 1 === 0 ? v : parseFloat(v.toFixed(2))}`;
  };
  if (!result) {
    return <div className="rc-result rc-result--empty"><span>{empty}</span></div>;
  }
  return (
    <div className="rc-result">
      <div className="rc-result-total">{fmt(result.total)}</div>
      <div className="rc-result-steps">
        {result.steps.map((s, i) => (
          <div key={i} className={`rc-result-step${s.isFinal ? ' rc-result-step--final' : ''}`}>
            <span className="rc-result-step-label">{s.label}</span>
            {s.detail && <span className="rc-result-step-detail">{s.detail}</span>}
            <span className="rc-result-step-amount">{fmt(s.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Calculator ────────────────────────────────────────────────────────────────

function Calculator({ rc }) {
  const { currency, unit, fixed, ratePerUnit, tiers, minMax } = rc;

  const [type,      setType]      = useState('graduated');
  const [fixedPx,   setFixedPx]   = useState(String(fixed.price));
  const [rpuRate,   setRpuRate]   = useState(String(ratePerUnit.rate));
  const [gradTiers, setGradTiers] = useState(() => tiers.map((t) => ({ ...t })));
  const [volTiers,  setVolTiers]  = useState(() => tiers.map((t) => ({ ...t })));
  const [mmFlat,    setMmFlat]    = useState(String(minMax.defaultFlatFee));
  const [mmRate,    setMmRate]    = useState(String(minMax.defaultRate));
  const [mmVariant, setMmVariant] = useState(minMax.defaultVariant);
  const [usageStr,  setUsageStr]  = useState('');

  useEffect(() => {
    setFixedPx(String(fixed.price));
    setRpuRate(String(ratePerUnit.rate));
    setGradTiers(tiers.map((t) => ({ ...t })));
    setVolTiers(tiers.map((t) => ({ ...t })));
    setMmFlat(String(minMax.defaultFlatFee));
    setMmRate(String(minMax.defaultRate));
    setMmVariant(minMax.defaultVariant);
    setUsageStr('');
  }, [rc]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    setFixedPx(String(fixed.price)); setRpuRate(String(ratePerUnit.rate));
    setGradTiers(tiers.map((t) => ({ ...t }))); setVolTiers(tiers.map((t) => ({ ...t })));
    setMmFlat(String(minMax.defaultFlatFee)); setMmRate(String(minMax.defaultRate));
    setMmVariant(minMax.defaultVariant); setUsageStr('');
  };

  const usage = parseFloat(usageStr);
  const hasUsage = usageStr !== '' && !isNaN(usage) && usage >= 0;

  let result = null;
  if (type === 'fixed')                   result = runFixed(parseFloat(fixedPx) || 0);
  else if (hasUsage) {
    if (type === 'ratePerUnit') result = runRatePerUnit(parseFloat(rpuRate) || 0, usage);
    if (type === 'graduated')   result = runGraduated(gradTiers, usage);
    if (type === 'volume')      result = runVolume(volTiers, usage);
    if (type === 'minMax')      result = runMinMax(mmFlat, mmRate, mmVariant, usage);
  }

  return (
    <div className="rc-calc">
      <div className="rc-calc-tabs">
        {CALC_TYPES.map((t) => (
          <button key={t.id}
            className={`rc-calc-tab${type === t.id ? ' rc-calc-tab--on' : ''}`}
            onClick={() => setType(t.id)}>{t.label}</button>
        ))}
      </div>

      <div className="rc-calc-body">
        <div className="rc-calc-inputs">
          {type === 'fixed' && (
            <label className="rc-field">
              <span className="rc-field-label">Fixed price ({currency})</span>
              <input className="rc-input" type="number" min="0" value={fixedPx}
                onChange={(e) => setFixedPx(e.target.value)} />
            </label>
          )}
          {type === 'ratePerUnit' && (
            <label className="rc-field">
              <span className="rc-field-label">Rate per {unit.replace(/ies$/, 'y').replace(/s$/, '')} ({currency})</span>
              <input className="rc-input" type="number" min="0" step="0.01" value={rpuRate}
                onChange={(e) => setRpuRate(e.target.value)} />
            </label>
          )}
          {type === 'graduated' && (
            <div className="rc-field">
              <span className="rc-field-label">Tiers (rate in {currency})</span>
              <TierEditor tiers={gradTiers} onChange={setGradTiers} allowRepeat />
            </div>
          )}
          {type === 'volume' && (
            <div className="rc-field">
              <span className="rc-field-label">Tiers (rate in {currency})</span>
              <TierEditor tiers={volTiers} onChange={setVolTiers} />
            </div>
          )}
          {type === 'minMax' && (
            <div className="rc-minmax-inputs">
              <label className="rc-field">
                <span className="rc-field-label">Flat fee ({currency})</span>
                <input className="rc-input" type="number" min="0" value={mmFlat}
                  onChange={(e) => setMmFlat(e.target.value)} />
              </label>
              <label className="rc-field">
                <span className="rc-field-label">Rate per {unit.replace(/ies$/, 'y').replace(/s$/, '')} ({currency})</span>
                <input className="rc-input" type="number" min="0" step="0.01" value={mmRate}
                  onChange={(e) => setMmRate(e.target.value)} />
              </label>
              <div className="rc-field">
                <span className="rc-field-label">Logic</span>
                <div className="rc-variant-btns">
                  {[['max', 'Maximum (floor)'], ['min', 'Minimum (cap)']].map(([v, label]) => (
                    <button key={v}
                      className={`rc-variant-btn${mmVariant === v ? ' rc-variant-btn--on' : ''}`}
                      onClick={() => setMmVariant(v)}>{label}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {type !== 'fixed' && (
            <label className="rc-field rc-field--usage">
              <span className="rc-field-label">Usage ({unit})</span>
              <input className="rc-input rc-input--usage" type="number" min="0"
                placeholder="e.g. 75" value={usageStr}
                onChange={(e) => setUsageStr(e.target.value)} />
            </label>
          )}
          {type === 'fixed' && <p className="rc-fixed-note">Fixed price is independent of usage.</p>}
          <button className="rc-reset" onClick={reset}>Reset to defaults</button>
        </div>

        <ResultPanel
          result={result}
          currency={currency}
          empty={type === 'fixed' ? '' : 'Enter a usage value to see the breakdown'}
        />
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function RateCardCalculator({ onNavigate }) {
  const { industry } = useIndustry();
  const rc = industry.rateCard;

  return (
    <DocPage toc={[]}>
      <PageHeader
        eyebrow="Billing · Rate Card"
        title="Calculator"
        description="Select a pricing model, adjust the parameters, and enter a usage value to see the calculated charge with a step-by-step breakdown."
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'rate-card',      label: 'Rate Cards' },
          { id: 'pricing-models', label: 'Pricing Models' },
        ]}
      />

      <Section id="calculator" title="Calculator">
        <Para>
          Pre-populated with {industry.label.toLowerCase()} defaults for{' '}
          <em>{rc.feeComponent}</em>. All parameters are editable.
        </Para>
        <Calculator rc={rc} />
      </Section>
    </DocPage>
  );
}
